import React, { useMemo, useState } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Descriptions,
  Alert,
  Empty,
  Spin,
  Row,
  Col,
  Statistic,
  Switch,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FormOutlined,
  ClockCircleOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { functions } from '@/shared/lib/supabase';
import { useFormSubmissions, type FormSubmissionRecord } from '../hooks/useFormSubmissions';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Title, Text } = Typography;
const { TextArea } = Input;

type FieldDefinition = {
  id?: string;
  name?: string;
  label?: string;
  n8n_name?: string;
  type?: string;
  options?: any[];
};

const guessFieldType = (value: any): string => {
  if (Array.isArray(value)) return 'multi-select';
  switch (typeof value) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'text';
  }
};

const deriveFieldDefinitions = (submission: FormSubmissionRecord | null): FieldDefinition[] => {
  if (!submission) return [];

  const candidateSources = submission.product?.product_form?.form_fields
    ?? submission.product?.form_fields
    ?? submission.form?.fields
    ?? null;

  let fields: FieldDefinition[] = [];
  if (Array.isArray(candidateSources)) {
    fields = candidateSources.map((field: any) => ({ ...field }));
  } else if (candidateSources && typeof candidateSources === 'object') {
    fields = Object.entries(candidateSources).map(([id, value]: any) => ({ id, ...(value ?? {}) }));
  }

  const canonical = Array.isArray(submission.form?.fields) ? submission.form?.fields : [];
  const mergeWithCanonical = (field: any) => {
    const match = canonical.find((cf: any) =>
      (field.id && (cf.id === field.id)) ||
      (field.name && (cf.name === field.name)) ||
      (field.n8n_name && (cf.n8n_name === field.n8n_name))
    );

    return {
      ...field,
      label: field.label ?? match?.label ?? field.name ?? field.n8n_name ?? field.id,
      n8n_name: field.n8n_name ?? match?.n8n_name ?? field.name ?? field.id,
      type: field.type ?? match?.type ?? 'text',
      options: field.options ?? match?.options,
    } as FieldDefinition;
  };

  if (fields.length) {
    fields = fields.map(mergeWithCanonical);
  } else if (canonical.length) {
    fields = canonical.map((field: any) => ({
      ...field,
      label: field.label ?? field.name ?? field.n8n_name ?? field.id,
    }));
  }

  if (!fields.length && submission.formData && typeof submission.formData === 'object') {
    fields = Object.keys(submission.formData).map((key) => ({
      id: key,
      label: key,
      type: guessFieldType(submission.formData?.[key]),
    }));
  }

  const seen = new Set<string>();
  fields = fields.filter((field) => {
    const key = field.id || field.name || field.n8n_name;
    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return fields;
};

const FormApprovalsPage: React.FC = () => {
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmissionRecord | null>(null);
  const [reviewForm] = Form.useForm();
  const [serverError, setServerError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<{ general?: boolean | null; client?: boolean | null } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  const queryClient = useQueryClient();

  const resolvedFields = useMemo(() => deriveFieldDefinitions(selectedSubmission), [selectedSubmission]);
  const formFieldsToRender = useMemo<FieldDefinition[]>(() => {
    if (resolvedFields.length) return resolvedFields;
    if (selectedSubmission?.formData) {
      return Object.keys(selectedSubmission.formData).map((key) => ({
        id: key,
        label: key,
        type: guessFieldType(selectedSubmission.formData?.[key]),
      } as FieldDefinition));
    }
    return [];
  }, [resolvedFields, selectedSubmission]);

  // Fetch submissions via edge function (service-role aggregation)
  const { data: submissions = [], isLoading } = useFormSubmissions({
    includeArchived: showArchived,
  });

  // Status options for filtering
  const statusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'pending_submission', label: 'Aguardando Preenchimento' },
    { value: 'submitted', label: 'Em Análise' },
    { value: 'approved', label: 'Aprovado' },
    { value: 'rejected', label: 'Rejeitado' },
  ];

  // Filter submissions by status
  const filteredSubmissions =
    statusFilter === 'all'
      ? submissions
      : submissions.filter(
          (submission) => submission.clientProduct.form_approval_status === statusFilter
        );

  // Update form status mutation
  const processSubmission = useMutation({
    mutationFn: async ({
      clientProductId,
      action,
      rejectionReason,
    }: {
      clientProductId: string;
      action: 'approve' | 'reject' | 'archive';
      rejectionReason?: string;
    }) => {
      return functions.invoke('process-form-submission', {
        body: {
          client_product_id: clientProductId,
          action,
          rejection_reason: rejectionReason,
        },
      });
    },
    onSuccess: (data: any, variables) => {
      const successMessages: Record<string, string> = {
        approve: 'Formulário aprovado com sucesso!',
        reject: 'Formulário rejeitado com sucesso!',
        archive: 'Formulário arquivado.',
      };

      message.success(successMessages[variables.action]);
      if (data?.db_update_error) {
        setServerError(String(data.db_update_error));
        message.warning('Aprovado, porém houve um problema ao salvar metadados no banco: ' + String(data.db_update_error));
      } else {
        setServerError(null);
      }
      if (data?.sync) {
        setSyncResult({ general: data.sync.general, client: data.sync.client });
        if (!data.sync.general && !data.sync.client) {
          message.warning('Aprovado, mas sincronização com n8n falhou. Verifique os logs do servidor.');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['admin-form-submissions'], exact: false });

      if (variables.action !== 'archive') {
        setReviewModalVisible(false);
        setSelectedSubmission(null);
        reviewForm.resetFields();
      }
    },
    onError: (error: any) => {
      console.error('Error processing form submission:', error);
      message.error('Erro ao processar formulário: ' + (error?.message || ' desconhecido'));
    },
  });

  // Allow saving submission data via the same process-form-submission function with action 'update'
  const updateSubmissionData = useMutation({
    mutationFn: async (vars: any) => {
      const { clientProductId, formData } = vars;
      return functions.invoke('process-form-submission', {
        body: {
          client_product_id: clientProductId,
          action: 'update',
          form_data: formData,
        },
      });
    },
    onSuccess: (response: any, variables: any) => {
      // If backend returned updated form submission, update local state for immediate feedback
      if (response?.updated_form_submission) {
        try {
          const newFormData = response.updated_form_submission?.form_data ?? response.updated_form_submission?.data ?? null;
          if (newFormData && selectedSubmission) {
            setSelectedSubmission({ ...selectedSubmission, formData: newFormData });
            try {
              reviewForm.setFieldsValue(newFormData);
            } catch (e) {
              console.warn('Could not reapply form values to the form after update', e);
            }
          }
        } catch (e) {
          console.warn('Could not apply updated_form_submission to local state', e);
        }
      }
      message.success('Dados do formulário atualizados com sucesso');
      // show db errors if present
      console.log('updateSubmissionData.onSuccess response:', response);
      if (response?.db_update_error) {
        setServerError(String(response.db_update_error));
        message.warning('Atualizado, porém houve um problema ao salvar metadados no banco: ' + String(response.db_update_error));
      } else {
        setServerError(null);
      }
      if (response?.submission_update_error) {
        setServerError(String(response.submission_update_error));
        message.warning('Não foi possível salvar os dados do formulário no banco: ' + String(response.submission_update_error));
      }
      if (response?.sync) {
        setSyncResult({ general: response.sync.general, client: response.sync.client });
        if (response.sync.client === false) {
          message.warning('Dados atualizados, porém sincronização com n8n falhou. Verifique os logs do servidor.');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['admin-form-submissions'], exact: false });
      const shouldClose = variables?._closeModal !== false;
      if (shouldClose) {
        setReviewModalVisible(false);
        setSelectedSubmission(null);
        reviewForm.resetFields();
      }
    },
    onError: (err: any) => {
      console.error('Error updating submission data:', err);
      message.error('Erro ao salvar alterações do formulário');
    },
  });

  const handleReview = (submission: FormSubmissionRecord) => {
    setSelectedSubmission(submission);
    const values: Record<string, any> = {};
    const formData = submission.formData ?? {};

    const submissionFields = deriveFieldDefinitions(submission);
    if (submissionFields.length) {
      for (const field of submissionFields) {
        const key = field.id || field.name || field.n8n_name;
        if (!key) continue;
        values[key] = formData[field.id ?? key]
          ?? (field.name ? formData[field.name] : undefined)
          ?? (field.n8n_name ? formData[field.n8n_name] : undefined)
          ?? undefined;
      }
    } else {
      Object.assign(values, formData);
    }

    for (const dataKey of Object.keys(formData)) {
      if (!(dataKey in values)) {
        values[dataKey] = formData[dataKey];
      }
    }

    const rejectionReason = submission.clientProduct?.rejection_reason ?? submission.submission?.rejection_reason ?? undefined;
    if (rejectionReason) values['rejectionReason'] = rejectionReason;
    reviewForm.setFieldsValue(values);
    setReviewModalVisible(true);
  };

  const handleSaveChanges = async () => {
    if (!selectedSubmission) return;
    try {
      const fieldIdsFromMeta = resolvedFields
        .map((field) => field.id || field.name || field.n8n_name)
        .filter((key): key is string => Boolean(key));

      const fallbackIds = !fieldIdsFromMeta.length && selectedSubmission.formData
        ? Object.keys(selectedSubmission.formData)
        : [];

      const targetFieldIds = fieldIdsFromMeta.length ? fieldIdsFromMeta : fallbackIds;

      if (!targetFieldIds.length) {
        message.warning('Nenhum campo disponível para salvar.');
        return;
      }

      await reviewForm.validateFields(targetFieldIds);
      const values = reviewForm.getFieldsValue(targetFieldIds);

      const payload: Record<string, any> = {};
      for (const fieldId of targetFieldIds) {
        if (fieldId === 'rejectionReason') continue;
        payload[fieldId] = values?.[fieldId] ?? null;
      }

      // Merge with existing submission data to avoid overwriting other fields
      const existingData = selectedSubmission?.formData ?? {};
      const mergedData = { ...existingData, ...payload };

      console.log('About to call updateSubmissionData.mutateAsync with payload:', { clientProductId: selectedSubmission.clientProduct.id, payload: mergedData });
      const resp = await updateSubmissionData.mutateAsync({
        clientProductId: selectedSubmission.clientProduct.id,
        formData: mergedData,
        _closeModal: true,
      });
      console.log('updateSubmissionData.mutateAsync returned:', resp);
    } catch (err) {
      console.error('Validation or update error:', err);
    }
  };

  const handleApprove = async () => {
    if (!selectedSubmission) return;

    // If the admin edited the form (unsaved), attempt to save first
    try {
      const fieldIdsFromMeta = resolvedFields
        .map((field) => field.id || field.name || field.n8n_name)
        .filter((key): key is string => Boolean(key));
      const fallbackIds = !fieldIdsFromMeta.length && selectedSubmission.formData
        ? Object.keys(selectedSubmission.formData)
        : [];
      const targetFieldIds = fieldIdsFromMeta.length ? fieldIdsFromMeta : fallbackIds;
      if (targetFieldIds.length) {
        await reviewForm.validateFields(targetFieldIds);
        const values = reviewForm.getFieldsValue(targetFieldIds);
        const payload: Record<string, any> = {};
        for (const fieldId of targetFieldIds) {
          if (fieldId === 'rejectionReason') continue;
          payload[fieldId] = values?.[fieldId] ?? null;
        }
        const mergedData = { ...(selectedSubmission.formData ?? {}), ...payload };
        await updateSubmissionData.mutateAsync({ clientProductId: selectedSubmission.clientProduct.id, formData: mergedData, _closeModal: false });
      }
    } catch (e) {
      console.warn('Error saving changes before approve:', e);
    }

    const result: any = await processSubmission.mutateAsync({
      clientProductId: selectedSubmission.clientProduct.id,
      action: 'approve',
    });

    // If function returned db_update_error or sync failures, show a brief inline alert in the modal
    if (result?.db_update_error) {
      setServerError(String(result.db_update_error));
    }
    if (result?.sync) {
      setSyncResult({ general: result.sync.general, client: result.sync.client });
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission) return;

    try {
      const values = await reviewForm.validateFields(['rejectionReason']);
      
      if (!values.rejectionReason || values.rejectionReason.trim() === '') {
        message.error('Por favor, informe o motivo da rejeição');
        return;
      }
      
      await processSubmission.mutateAsync({
        clientProductId: selectedSubmission.clientProduct.id,
        action: 'reject',
        rejectionReason: values.rejectionReason,
      });
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const handleArchive = (submission: FormSubmissionRecord) => {
    Modal.confirm({
      title: 'Arquivar formulário',
      content: 'Deseja realmente arquivar este formulário? Ele será ocultado da lista por padrão.',
      okText: 'Arquivar',
      cancelText: 'Cancelar',
      onOk: () =>
        processSubmission.mutateAsync({
          clientProductId: submission.clientProduct.id,
          action: 'archive',
        }),
    });
  };

  const isArchived = (record: FormSubmissionRecord) => {
    return !!(record.clientProduct.form_archived_at || record.submission?.archived_at);
  };

  const getStatusTag = (record: FormSubmissionRecord) => {
    if (isArchived(record)) {
      return <Tag color="purple">Arquivado</Tag>;
    }

    const status = record.clientProduct.form_approval_status || 'pending_submission';
    const statusMap: Record<string, { color: string; text: string }> = {
      pending_submission: { color: 'default', text: 'Aguardando Preenchimento' },
      submitted: { color: 'processing', text: 'Em Análise' },
      approved: { color: 'success', text: 'Aprovado' },
      rejected: { color: 'error', text: 'Rejeitado' },
    };

    const { color, text } = statusMap[status] || { color: 'default', text: status };
    return <Tag color={color}>{text}</Tag>;
  };

  const columns = [
    {
      title: 'Cliente',
      key: 'client',
      render: (_: any, record: FormSubmissionRecord) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.userProfile.full_name || 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.userProfile.company_name || 'Sem empresa'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Produto',
      dataIndex: ['product', 'name'],
      key: 'product',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: FormSubmissionRecord) => getStatusTag(record),
    },
    {
      title: 'Data de Submissão',
      key: 'submitted_at',
      render: (_: any, record: FormSubmissionRecord) => {
        const submittedAt = record.submission?.submitted_at || record.submission?.created_at || record.formData?.submitted_at;
        return submittedAt ? dayjs(submittedAt).format('DD/MM/YYYY HH:mm') : '-';
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: any, record: FormSubmissionRecord) => (
        <Space>
          <Button type="primary" icon={<EyeOutlined />} onClick={() => handleReview(record)}>
            Revisar
          </Button>
          {record.clientProduct.form_approval_status === 'approved' &&
            !isArchived(record) && (
              <Button
                icon={<InboxOutlined />}
                onClick={() => handleArchive(record)}
                loading={processSubmission.isPending}
              >
                Arquivar
              </Button>
            )}
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const stats = {
    total: submissions.length,
    pending: submissions.filter(
      (submission) => submission.clientProduct.form_approval_status === 'submitted'
    ).length,
    awaiting: submissions.filter(
      (submission) => submission.clientProduct.form_approval_status === 'pending_submission'
    ).length,
    archived: submissions.filter((submission) => submission.clientProduct.form_archived_at).length,
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <FormOutlined /> Aprovações de Formulários
      </Title>
      <Text type="secondary">
        Revise e aprove os formulários submetidos pelos clientes
      </Text>

      <Row gutter={16} style={{ marginTop: '24px', marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total de Submissões"
              value={stats.total}
              prefix={<FormOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Aguardando Preenchimento"
              value={stats.awaiting}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Aguardando Análise"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Arquivados"
              value={stats.archived}
              prefix={<InboxOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Space>
            <Text strong>Status:</Text>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ minWidth: 180, padding: '4px 8px', borderRadius: 4 }}
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Space>
          <Space>
            <Text strong>Mostrar Arquivados</Text>
            <Switch checked={showArchived} onChange={setShowArchived} />
          </Space>
        </Space>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : filteredSubmissions.length > 0 ? (
          <Table
            columns={columns}
            dataSource={filteredSubmissions}
            rowKey={(record) => record.clientProduct.id}
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <Empty
            description="Nenhuma submissão encontrada"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Review Modal */}
      <Modal
        title="Revisar Submissão de Formulário"
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          setSelectedSubmission(null);
          reviewForm.resetFields();
        }}
        width={800}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setReviewModalVisible(false);
              setSelectedSubmission(null);
              reviewForm.resetFields();
            }}
          >
            Cancelar
          </Button>
          ,
          <Button
            key="save"
            type="default"
            icon={<EyeOutlined />}
            onClick={handleSaveChanges}
            loading={updateSubmissionData.isPending}
          >
            Salvar
          </Button>
          ,
          <Button
            key="reject"
            danger
            icon={<CloseCircleOutlined />}
            onClick={handleReject}
            loading={processSubmission.isPending}
            disabled={!selectedSubmission?.clientProduct?.requires_form_approval}
          >
            Rejeitar
          </Button>
          ,
          <Button
            key="approve"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleApprove}
            loading={processSubmission.isPending}
            disabled={
              !selectedSubmission?.clientProduct?.requires_form_approval ||
              !selectedSubmission?.clientProduct?.product?.n8n_instance_id ||
              !(selectedSubmission?.clientProduct?.custom_workflow_id || selectedSubmission?.clientProduct?.cloned_workflow_id)
            }
          >
            Aprovar
          </Button>,
        ]}
      >
        {selectedSubmission && (
          <>
            <Alert
              message="Informações do Cliente"
              description={
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Nome">
                    {selectedSubmission.userProfile.full_name || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Empresa">
                    {selectedSubmission.userProfile.company_name || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Produto">
                    {selectedSubmission.product.name}
                  </Descriptions.Item>
                </Descriptions>
              }
              type="info"
              style={{ marginBottom: '16px' }}
            />
            {/* If the submission contains known missing fields, show them */}
            {selectedSubmission?.submission?.metadata?.missing_fields && Array.isArray(selectedSubmission.submission.metadata.missing_fields) && selectedSubmission.submission.metadata.missing_fields.length > 0 && (
              <Alert
                type="warning"
                message="Campos faltando"
                description={`Os seguintes campos não foram preenchidos pelo cliente: ${selectedSubmission.submission.metadata.missing_fields.join(', ')}`}
                style={{ marginBottom: 12 }}
              />
            )}
            {!selectedSubmission?.clientProduct?.requires_form_approval && (
              <Alert
                type="warning"
                message="Aprovação não aplicável"
                description="Este formulário não exige aprovação automática; as ações Aprovar/Rejeitar foram desativadas para evitar mudanças acidentais."
                style={{ marginBottom: 12 }}
              />
            )}
            {selectedSubmission?.clientProduct?.requires_form_approval && (
              !selectedSubmission?.clientProduct?.product?.n8n_instance_id ||
              !(selectedSubmission?.clientProduct?.custom_workflow_id || selectedSubmission?.clientProduct?.cloned_workflow_id) ? (
                <Alert
                  type="warning"
                  message="Aprovar indisponível"
                  description="Este formulário não pode ser aprovado porque o produto não tem um workflow configurado no n8n ou a instância do n8n está ausente."
                  style={{ marginBottom: 12 }}
                />
              ) : null
            )}

            <Card title="Dados do Formulário" size="small">
              {serverError && (
                <Alert
                  type="warning"
                  message="Problema ao salvar metadados"
                  description={serverError}
                  style={{ marginBottom: 12 }}
                />
              )}
              {syncResult && (syncResult.general === false && syncResult.client === false) && (
                <Alert
                  type="warning"
                  message="Falha na sincronização com n8n"
                  description="Os campos do workflow não foram atualizados. Verifique o endpoint do n8n configurado para o produto."
                  style={{ marginBottom: 12 }}
                />
              )}
              {syncResult && syncResult.client === false && (
                <Alert
                  type="warning"
                  message="Falha na sincronização com n8n (Cliente)"
                  description="As alterações foram salvas, mas o nó Cliente - Parametros do workflow não foi atualizado. Verifique o endpoint do n8n configurado para o produto."
                  style={{ marginBottom: 12 }}
                />
              )}

              <Form form={reviewForm} layout="vertical">
                {formFieldsToRender.length ? formFieldsToRender.map((field) => {
                  const fieldId = field.id || field.name || field.n8n_name;
                  if (!fieldId) return null;
                  const label = field.label || field.n8n_name || field.name || fieldId;
                  const fieldType = (field.type || 'text').toLowerCase();
                  const renderSelectOptions = () => {
                    if (!Array.isArray(field.options)) return null;
                    return field.options.map((opt: any) => {
                      if (typeof opt === 'object' && opt !== null) {
                        const optionValue = opt.value ?? opt.id ?? opt.key ?? opt.label;
                        const optionLabel = opt.label ?? String(optionValue ?? '');
                        return (
                          <Select.Option key={`${fieldId}-${optionValue}`} value={optionValue}>
                            {optionLabel}
                          </Select.Option>
                        );
                      }
                      return (
                        <Select.Option key={`${fieldId}-${opt}`} value={opt}>
                          {String(opt)}
                        </Select.Option>
                      );
                    });
                  };

                  switch (fieldType) {
                    case 'textarea':
                      return (
                        <Form.Item key={fieldId} name={fieldId} label={label}>
                          <TextArea rows={3} />
                        </Form.Item>
                      );
                    case 'number':
                      return (
                        <Form.Item key={fieldId} name={fieldId} label={label}>
                          <Input type="number" />
                        </Form.Item>
                      );
                    case 'select':
                      return (
                        <Form.Item key={fieldId} name={fieldId} label={label}>
                          <Select>{renderSelectOptions()}</Select>
                        </Form.Item>
                      );
                    case 'multi-select':
                    case 'multiselect':
                    case 'multi_select':
                      return (
                        <Form.Item key={fieldId} name={fieldId} label={label}>
                          <Select mode="multiple" allowClear>
                            {renderSelectOptions()}
                          </Select>
                        </Form.Item>
                      );
                    case 'boolean':
                    case 'checkbox':
                      return (
                        <Form.Item key={fieldId} name={fieldId} label={label} valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      );
                    default:
                      return (
                        <Form.Item key={fieldId} name={fieldId} label={label}>
                          <Input />
                        </Form.Item>
                      );
                  }
                }) : (
                  <Alert
                    type="info"
                    message="Nenhum campo disponível"
                    description="Não foi possível carregar os campos deste formulário."
                  />
                )}
                <Form.Item
                  name="rejectionReason"
                  label="Motivo da Rejeição"
                  rules={[
                    {
                      required: true,
                      message: 'Por favor, informe o motivo da rejeição',
                    },
                    {
                      min: 10,
                      message: 'O motivo deve ter pelo menos 10 caracteres',
                    },
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva o motivo da rejeição para que o cliente possa corrigir..."
                  />
                </Form.Item>
              </Form>
            </Card>

              
          </>
        )}
      </Modal>
    </div>
  );
};

export default FormApprovalsPage;
